import { useFormStatus } from 'react-dom';
import { BUTTONS, LABELS, MESAJE, PAGE_METADATA, FORM_MESSAGES, AUTH_UI } from "@buzunarul/shared-constants";
// 🔐 ACCESS CONTROL: Import pentru controlul accesului în beta testing
import { ACCESS_CONTROL, BETA_UI, DEPLOYMENT_CONFIG } from "@buzunarul/shared-constants/access-control";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router";
import { useAuthStore } from "../../../stores/authStore";

// Document metadata
import { useDocumentMeta } from "../../../hooks/useDocumentMeta";

// CVA styling imports - UNIFIED MIGRATION
import { button, card, cn, spaceY } from "../../../styles/cva-v2";

// Form utilities
import { createFormAction } from "../../../utils/formActions";
import { useFormAction } from "../../../hooks/useFormAction";

import Alert from "../../primitives/Alert/Alert";
import Input from "../../primitives/Input/Input";
import { GoogleButton } from "../../primitives/GoogleButton";


/**
 * Submit button component using useFormStatus
 */
function SubmitButton({ isFormValid }: { isFormValid: boolean }) {
  const { pending } = useFormStatus();
  
  return (
    <button
      type="submit"
      disabled={!isFormValid || pending}
      className={cn(
        button({ variant: "primary", size: "md" }),
        "w-full",
        !isFormValid && "opacity-50 cursor-not-allowed"
      )}
      data-testid="register-submit"
    >
      {pending ? FORM_MESSAGES.SUBMITTING : BUTTONS.REGISTER}
    </button>
  );
}

/**
 * Register form action
 */
const registerAction = createFormAction<{ email: string; password: string; confirmPassword: string }>(
  async (data) => {
    const { register } = useAuthStore.getState();
    
    // Client-side validation for password match
    if (data.password !== data.confirmPassword) {
      return {
        error: MESAJE.PAROLE_NECORESPUNZATOARE || "Parolele nu se potrivesc!",
        success: false
      };
    }
    
    // 🔐 VERIFICARE ACCESS CONTROL: Blochează înregistrarea pentru email-uri neaprobate
    if (!DEPLOYMENT_CONFIG.isEmailApproved(data.email)) {
      return {
        error: ACCESS_CONTROL.REGISTRATION_RESTRICTED,
        success: false
      };
    }
    
    // Call the register function from auth store
    await register(data.email, data.password);
    
    // Check if registration was successful
    const { error } = useAuthStore.getState();
    
    if (error) {
      // Map error types to user-friendly messages
      let errorMessage = error;
      if (error.includes("already")) {
        errorMessage = "Email-ul este deja folosit.";
      } else if (error.includes("weak") || error.includes("short")) {
        errorMessage = "Parola este prea slabă. Trebuie să aibă cel puțin 6 caractere.";
      } else {
        errorMessage = error || MESAJE.REGISTER_ERROR;
      }
      
      return {
        error: errorMessage,
        success: false
      };
    }
    
    return {
      success: true,
      data: { email: data.email }
    };
  }
);

const RegisterForm = () => {
  // Document metadata
  useDocumentMeta({
    title: PAGE_METADATA.AUTH.REGISTER.title,
    description: PAGE_METADATA.AUTH.REGISTER.description,
  });

  const { loginWithGoogle, loading: googleLoading } = useAuthStore();
  
  // Local state for validation
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Use the form action hook
  const { formAction, isPending, error } = useFormAction(
    registerAction,
    {},
    {
      onSuccess: () => {
        setShowSuccess(true);
        toast.success(MESAJE.REGISTER_SUCCES);
        // Reset form
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        
        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      },
      onError: (error) => {
        toast.error(error);
      }
    }
  );

  // Funcție pentru validarea formatului de email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  // 🔐 VERIFICARE ACCESS CONTROL: Verifică dacă emailul este aprobat pentru testare
  const isEmailApproved = (email: string): boolean => {
    return DEPLOYMENT_CONFIG.isEmailApproved(email);
  };

  // Verifică dacă parolele se potrivesc
  const passwordsMatch = password === confirmPassword;

  // 🔐 ACCESS CONTROL: Verifică dacă formularul este valid INCLUSIV whitelist
  const isFormValid =
    isValidEmail(email) &&
    password.length >= 6 &&
    confirmPassword.length >= 6 &&
    passwordsMatch &&
    isEmailApproved(email); // 🚨 RESTRICȚIE BETA: Email trebuie să fie aprobat

  const handleGoogleRegister = async () => {
    try {
      await loginWithGoogle();
      // Nu navigăm aici pentru că Google OAuth va face redirect
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : MESAJE.GOOGLE_AUTH_ERROR;
      toast.error(errorMessage);
    }
  };

  return (
    <div className="w-full max-w-md">
      <form
        className={cn(
          card({ variant: "elevated" }),
          "w-full",
        )}
        action={formAction}
        data-testid="register-form"
      >
        <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Înregistrare</h2>
            {/* 🎯 BETA BADGE: Afișează că aplicația este în testare */}
            {DEPLOYMENT_CONFIG.IS_BETA_TESTING && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                {BETA_UI.BETA_BADGE}
              </span>
            )}
          </div>
        </div>

        <div className={cn("p-lg", spaceY({ spacing: 4 }))}>
          {/* 🛡️ BETA TESTING NOTICE: Afișează informația despre accesul restricționat */}
          {DEPLOYMENT_CONFIG.IS_BETA_TESTING && (
            <Alert variant="warning" data-testid="beta-testing-notice">
              <div className="space-y-2">
                <p className="font-medium">{ACCESS_CONTROL.BETA_TESTING_NOTICE}</p>
                <p className="text-sm">{ACCESS_CONTROL.APPROVED_DOMAINS}</p>
                <p className="text-sm text-gray-600">{ACCESS_CONTROL.CONTACT_ADMIN}</p>
              </div>
            </Alert>
          )}

          {/* Email Input */}
          <Input
            id="email"
            name="email"
            type="email"
            label={`${LABELS.EMAIL}*`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            variant="default"
            data-testid="register-email"
          />

          {/* 🛡️ EMAIL VERIFICATION: Afișează starea verificării email-ului */}
          {email && isValidEmail(email) && (
            <div className="text-sm">
              {isEmailApproved(email) ? (
                <div className="flex items-center text-green-600">
                  <span className="mr-2">✅</span>
                  {AUTH_UI.EMAIL_APPROVED}
                </div>
              ) : (
                <div className="flex items-center text-amber-600">
                  <span className="mr-2">⚠️</span>
                  {AUTH_UI.EMAIL_NOT_APPROVED}
                </div>
              )}
            </div>
          )}

          {/* Password Input */}
          <Input
            id="password"
            name="password"
            type="password"
            label={`${LABELS.PAROLA}* ${AUTH_UI.PASSWORD_MIN_CHARS}`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            variant="default"
            data-testid="register-password"
          />

          {/* Confirm Password Input */}
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            label={`${LABELS.CONFIRMA_PAROLA}*`}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            variant="default"
            data-testid="register-confirm-password"
          />

          {/* Error Message */}
          {error && (
            <Alert
              variant="error"
              data-testid="register-error"
            >
              {error}
            </Alert>
          )}

          {/* Success Message */}
          {showSuccess && (
            <Alert
              variant="success"
              data-testid="register-success"
            >
              {MESAJE.REGISTER_SUCCES}
            </Alert>
          )}

          {/* Submit Button with useFormStatus */}
          <SubmitButton isFormValid={isFormValid} />

          {/* Divider */}
          <div className="relative">
            <div className="flex absolute inset-0 items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="flex relative justify-center text-sm">
              <span className="px-2 text-gray-500 bg-white">{AUTH_UI.DIVIDER_OR}</span>
            </div>
          </div>

          {/* Google Register Button */}
          <GoogleButton
            onClick={handleGoogleRegister}
            isLoading={googleLoading || isPending}
            variant="register"
            data-testid="google-register-button"
          />
          
          {/* Google Auth Explanation */}
          <p className="text-xs text-center text-gray-500">
            {AUTH_UI.GOOGLE_AUTO_CREATE_REGISTER}
          </p>

          {/* Login Link */}
          <div className="text-center">
            <Link
              to="/login"
              className={cn(
                button({ variant: "ghost", size: "sm" }),
                "text-blue-600 hover:text-blue-700",
              )}
              data-testid="switch-to-login"
            >
              {AUTH_UI.SWITCH_TO_LOGIN}
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;